/**
 * Appointment participant lookup — single source of truth for "which
 * participant is the patient".
 *
 * Why this exists: an appointment's patient is NOT always recorded under
 * `referenceType: "Patient"`. **Minors are attached as `"RelatedPerson"`**
 * (the guardian-mediated booking), with the child's PatientResponse on that
 * participant's `.patient`. A strict `referenceType === 'patient'` lookup
 * therefore misses the patient entirely for every minor appointment — the
 * tile renders the name (the calendar mapper used the loose rule) but
 * open / reschedule / cancel / mark-arrived all read `patientId === undefined`
 * and silently bail. (Reproduced: Nader Hebela's "First Appointment" for
 * Arfaan Shaik, DOB 2025 — un-openable from the calendar.)
 *
 * Rule: prefer the canonical `Patient` participant; if there isn't one, fall
 * back to any NON-provider participant that carries a `.patient` object. This
 * is a strict superset of the old `referenceType === 'patient'` check, so the
 * adult/Patient path is unchanged — it only additionally resolves minors.
 */

/**
 * Minimal structural shape — works with either ParticipantResponse copy and
 * the slimmer participant types (e.g. SeriesRescheduleParticipant), which
 * allow `null` on referenceType.
 */
interface PatientBearingParticipant {
  referenceType?: string | null;
  patient?: unknown;
  provider?: unknown;
}

export function findPatientParticipant<T extends PatientBearingParticipant>(
  participants: readonly T[] | undefined | null,
): T | undefined {
  if (!participants) return undefined;
  // 1. Canonical Patient participant (adults, and minors booked directly).
  const exact = participants.find(
    (p) => (p.referenceType ?? '').toLowerCase() === 'patient',
  );
  if (exact) return exact;
  // 2. Fallback: any participant carrying a patient that isn't the provider.
  //    Minors arrive as `RelatedPerson` with the child on `.patient`.
  return participants.find((p) => {
    const rt = (p.referenceType ?? '').toLowerCase();
    return Boolean(p.patient) && rt !== 'provider' && rt !== 'practitioner';
  });
}

/** Minimal shape for the provider-bearing participant. `provider.id` is the
 *  provider's doctorSetupId on the wire (verified on dev — same id space the
 *  consent provider-signature lookup uses). */
interface ProviderBearingParticipant {
  referenceType?: string | null;
  provider?: { id?: string | null; name?: string | null } | null;
}

/**
 * The appointment's treating provider participant (`referenceType` provider /
 * practitioner carrying a `.provider`). Used by the consent fill flow on the
 * appointment Docs tab to auto-source that provider's signature (PROD-1243).
 */
export function findProviderParticipant<T extends ProviderBearingParticipant>(
  participants: readonly T[] | undefined | null,
): T | undefined {
  if (!participants) return undefined;
  return participants.find((p) => {
    const rt = (p.referenceType ?? '').toLowerCase();
    return Boolean(p.provider) && (rt === 'provider' || rt === 'practitioner');
  });
}
